import * as React from "react"
import {
  Modal,
  ModalCard,
  StyledModal,
  StyledModalBody,
  StyledModalActions,
  Button,
  StyledModalHeader,
  useShowSuccessToast,
} from "gatsby-interface"
import { Text } from "./shared/Text"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import {
  ui as uiText,
  gatsbyCloudCdnIntegrationConfig as text,
  errorAlert as errorText,
} from "@modules/locales/default.js"

import {
  useRemoveCloudCdnDomainMutation,
  HostingDetailsForSiteDocument,
} from "@modules/cdn/gatsbyCloudCdn/queries.generated"

import { FormattedMessage, commonMessageTags } from "@modules/locales"
import { interpolateMessage } from "@modules/locales"
import { PlatformLimitSummariesDocument } from "@modules/organization/queries.generated"

export type RemoveDomainFormProps = {
  siteId: string
  organizationId: string
  domain: string
  onDismiss: () => void
  onComplete: () => void
}

export function RemoveDomainForm({
  siteId,
  organizationId,
  domain,
  onDismiss,
  onComplete,
}: RemoveDomainFormProps) {
  const [removeCloudCdnDomainMutation] = useRemoveCloudCdnDomainMutation()
  const showSuccessToast = useShowSuccessToast()
  const [setError, errorAlert] = useTriggerErrorAlert()
  const [loading, setLoading] = React.useState(false)

  const handleRemove = async () => {
    setLoading(true)

    removeCloudCdnDomainMutation({
      variables: {
        siteId,
        domainName: domain,
      },
      refetchQueries: [
        {
          query: HostingDetailsForSiteDocument,
          variables: { siteId },
        },
        {
          query: PlatformLimitSummariesDocument,
          variables: {
            workspaceId: organizationId,
          },
        },
      ],
    })
      .then(({ data }) => {
        setLoading(false)
        const success = data?.removeCloudCdnDomain?.success ?? false

        if (success) {
          onComplete()
          return showSuccessToast(
            <FormattedMessage<"domain", "strong">
              message={text.messages.domainRemoved}
              values={{ domain }}
              tags={commonMessageTags}
            />
          )
        }

        const message =
          data?.removeCloudCdnDomain?.message ||
          errorText.messages.somethingWentWrong
        setError({ message })
      })
      .catch(e => {
        setLoading(false)
        setError(e)
      })
  }

  return (
    <Modal
      isOpen={Boolean(domain)}
      aria-label={interpolateMessage<"domain">(
        text.messages.doYouWantToRemoveDomain,
        { domain: domain }
      )}
      onDismiss={onDismiss}
      type="error"
    >
      <ModalCard>
        <StyledModal variant="WARNING">
          <StyledModalHeader onCloseButtonClick={onDismiss}>
            {text.headers.removeDomain}
          </StyledModalHeader>

          <StyledModalBody>
            <Text>
              <FormattedMessage<"domain", "strong">
                message={text.messages.areYouSureYouWantToRemoveDomain}
                values={{ domain }}
                tags={commonMessageTags}
              />
            </Text>

            {errorAlert && (
              <div
                css={theme => ({
                  marginTop: theme.space[5],
                })}
              >
                {errorAlert}
              </div>
            )}

            <StyledModalActions>
              <Button type="button" onClick={onDismiss} variant="SECONDARY">
                {uiText.actions.cancel}
              </Button>
              <Button
                type="button"
                onClick={handleRemove}
                tone="DANGER"
                variant="PRIMARY"
                loading={loading}
                loadingLabel={uiText.labels.removing}
              >
                {uiText.actions.remove}
              </Button>
            </StyledModalActions>
          </StyledModalBody>
        </StyledModal>
      </ModalCard>
    </Modal>
  )
}
